/**
 * Floating Voice Controls Component
 * Zoom/Meet-style persistent voice controls that float at bottom of screen
 * Always accessible during network games
 */

import { useState } from 'react'
import { ConnectionStatus, VoiceStatus } from '../hooks/usePeerJS'
import '../styles/FloatingVoiceControls.css'

interface FloatingVoiceControlsProps {
  connectionStatus: ConnectionStatus
  voiceStatus: VoiceStatus
  onToggleMute: () => void
  onConnect?: () => void
  onDisconnect?: () => void
}

export function FloatingVoiceControls({
  connectionStatus,
  voiceStatus,
  onToggleMute,
  onConnect,
  onDisconnect,
}: FloatingVoiceControlsProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return '#10b981' // green
      case 'connecting':
        return '#f59e0b' // orange
      case 'disconnected':
      case 'failed':
        return '#ef4444' // red
      default:
        return '#6b7280' // gray
    }
  }

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Voice Connected'
      case 'connecting':
        return 'Connecting...'
      case 'disconnected':
        return 'Voice Off'
      case 'failed':
        return 'Connection Failed'
      default:
        return 'Voice Off'
    }
  }

  return (
    <div className={`floating-voice-controls ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Compact Bar (always visible) */}
      <div className="voice-control-bar">
        {/* Status Indicator */}
        <button
          className="voice-status-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ '--status-color': getStatusColor() } as React.CSSProperties}
          title={getStatusText()}
        >
          <span className="status-dot" />
          <span className="status-text">{getStatusText()}</span>
          <span className="expand-icon">{isExpanded ? '▼' : '▲'}</span>
        </button>

        {/* Main Controls */}
        <div className="voice-main-controls">
          {/* Mute/Unmute */}
          <button
            className={`voice-control-btn ${voiceStatus.localMuted ? 'muted' : 'active'}`}
            onClick={onToggleMute}
            disabled={connectionStatus !== 'connected'}
            title={voiceStatus.localMuted ? 'Unmute microphone' : 'Mute microphone'}
          >
            <span className="btn-icon">{voiceStatus.localMuted ? '🔇' : '🎤'}</span>
            <span className="btn-label">{voiceStatus.localMuted ? 'Unmute' : 'Mute'}</span>
          </button>

          {/* Connect/Disconnect */}
          {connectionStatus === 'connected' ? (
            onDisconnect && (
              <button
                className="voice-control-btn disconnect"
                onClick={onDisconnect}
                title="Hang up voice call"
              >
                <span className="btn-icon">📞</span>
                <span className="btn-label">Hang Up</span>
              </button>
            )
          ) : (
            onConnect && (
              <button
                className="voice-control-btn connect"
                onClick={onConnect}
                title="Enable voice chat"
              >
                <span className="btn-icon">🎤</span>
                <span className="btn-label">
                  {connectionStatus === 'failed' ? 'Retry' : 'Join Voice'}
                </span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Expanded Info Panel */}
      {isExpanded && (
        <div className="voice-info-panel">
          {connectionStatus === 'connected' && (
            <div className="voice-info-content">
              <p className="info-text">
                {voiceStatus.remoteConnected
                  ? '✅ Both players connected'
                  : '⏳ Waiting for other player...'}
              </p>
            </div>
          )}

          {connectionStatus === 'connecting' && (
            <div className="voice-info-content">
              <div className="spinner-small" />
              <p className="info-text">Connecting to voice...</p>
              <small className="info-hint">Both players must enable voice chat</small>
            </div>
          )}

          {connectionStatus === 'failed' && (
            <div className="voice-info-content">
              <p className="info-text">⚠️ Connection timeout</p>
              <small className="info-hint">Try again when both players are ready</small>
            </div>
          )}

          {(connectionStatus === 'disconnected' || connectionStatus === 'closed') && (
            <div className="voice-info-content">
              <p className="info-text">Voice chat is off</p>
              <small className="info-hint">Click "Join Voice" to enable</small>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
